// Order picker: a warehouse-arrivals table on the left and an "Order builder"
// cart on the right. The client ticks orders (or adds one manually), reviews
// each pallet's Length × Width × Height (cm) and weight (kg), sets delivery
// windows, optionally edits/adds/removes pallets, then builds a plan.
//
// Units: dimensions in CENTIMETERS and weight in KILOGRAMS (the client's
// units). Dims convert to feet for the optimizer at submit; weight is already
// the optimizer's unit, so it passes through untouched.

import { type CSSProperties, useEffect, useRef, useState } from "react";

import {
  type AddedOrderPayload,
  type FleetOptions,
  type OrderSelectionPayload,
  deleteDraft,
  getDraft,
  getOrderPallets,
  listDrafts,
  listOrders,
  resolveOrders,
  saveDraft,
} from "../api/client";
import { todayIn } from "../lib/format";
import { useT } from "../lib/i18n";
import { STD_PALLET_CM, TIME_SLOTS, palletToCm, palletToFt } from "../lib/orderUnits";
import { STATUS_TONE } from "../lib/orderStatus";
import type {
  DispatchFilter,
  DraftSummary,
  OrderDispatch,
  OrderPallet,
  OrderSummary,
  PlanRoute,
  PlanTruck,
} from "../types";
import { Dialog } from "./Dialog";
import { TruckDepartures, useTruckDepartures } from "./TruckDepartures";

// Cart autosave: the whole composition is mirrored to localStorage on change
// so a submit error (which remounts this view) or a page reload recovers it.
// App clears the key on a successful run so "New plan" starts fresh.
export const CART_AUTOSAVE_KEY = "dispatch.cart.autosave.v1";

// A physical truck runs at most this many trips in a day, so the paste auto-fill
// rolls onto the next truck after every two blocks.
const MAX_TRIPS_PER_TRUCK = 2;

// Service regions offered in the picker filter. Names are rendered from the
// reader's locale (region.* in i18n), not sent by the backend, so a 中文
// dispatcher sees Chinese warehouse names. Keep in step with
// dispatch.config.regions.
const REGION_OPTIONS = ["socal", "east"] as const;

// Parse a pasted blob of order numbers into trip/truck-tagged entries. Numbers
// within a block split on any common separator; a BLANK LINE starts a new trip,
// so a dispatcher can paste "trip-1 orders ⏎⏎ trip-2 orders" and have each block
// land on its own trip. A truck runs at most MAX_TRIPS_PER_TRUCK trips, so blocks
// fill a truck before rolling to the next: block 1-2 → truck 1 (trip 1, 2), block
// 3-4 → truck 2 (trip 1, 2), … — the trip number resets per truck. Trimmed, blanks
// dropped, deduped case-insensitively (keeping the first-seen casing and trip/truck).
function parseOrderGroups(raw: string): { no: string; tripNo: number; truckNo: number }[] {
  const seen = new Set<string>();
  const out: { no: string; tripNo: number; truckNo: number }[] = [];
  let blockIndex = 0;
  // Normalize CRLF/CR (Excel / Windows paste) so blank lines split reliably.
  const text = raw.replace(/\r\n?/g, "\n");
  for (const block of text.split(/\n[ \t]*\n+/)) {
    const nos = block
      .split(/[\s,;]+/)
      .map((tok) => tok.trim())
      .filter(Boolean);
    if (nos.length === 0) continue; // skip empty blocks (leading/trailing breaks)
    const truckNo = Math.floor(blockIndex / MAX_TRIPS_PER_TRUCK) + 1;
    const tripNo = (blockIndex % MAX_TRIPS_PER_TRUCK) + 1;
    blockIndex += 1;
    for (const no of nos) {
      const key = no.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ no, tripNo, truckNo });
    }
  }
  return out;
}

interface CartPallet {
  length_cm: number;
  width_cm: number;
  height_cm: number;
  weight_kg: number;
  source: string;
}

interface SelState {
  manual: boolean;
  orderNo: string; // editable for manual orders
  destZip: string; // editable; seeded from the synced ZIP for DB orders
  customer: string | null;
  dimsSource: string | null; // null for manual
  deadlineClock: string;
  arrivalClock: string;
  tripNo: number; // client's trip assignment (orders sharing it = one trip)
  truckNo: number; // physical truck (trips sharing it run sequentially)
  pallets: CartPallet[] | null;
  loadingPallets: boolean;
  edited: boolean; // true => send `pallets` as an explicit list
}

function toCartPallet(p: OrderPallet): CartPallet {
  return { ...palletToCm(p), source: p.source };
}

const STD_PALLET = (): CartPallet => ({ ...STD_PALLET_CM, source: "override" });

// The Orders tab's status filter choices ("" = all orders).
const DISPATCH_FILTERS: DispatchFilter[] = ["available", "on_plan", "delivered", "exception"];

// A plan built by hand from the cart: its orders, delivery day, and, from
// Evaluate mode, the trucks and trips the cart grouped them on.
export interface HandBuiltPlan {
  orders: AddedOrderPayload[];
  planDate: string;
  document?: { trucks: PlanTruck[]; routes: PlanRoute[] };
}

export function OrdersView({
  onSubmit,
  onEvaluate,
  onPlanByHand,
  onOpenPlan,
  errorMessage,
}: {
  onSubmit: (orders: OrderSelectionPayload[], fleet: FleetOptions) => void;
  onEvaluate: (orders: OrderSelectionPayload[], fleet: FleetOptions) => void;
  // Builds a draft plan from the cart, with nothing optimized.
  onPlanByHand?: (plan: HandBuiltPlan) => void;
  // Opens a plan an order is on, at its truck.
  onOpenPlan?: (planId: string, truckId?: string) => void;
  errorMessage: string | null;
}) {
  const t = useT();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  // "" = every warehouse, which is what the picker did before regions
  // existed. A dispatcher narrows to their own to stop scrolling past the
  // other coast's arrivals.
  const [region, setRegion] = useState("");
  const [search, setSearch] = useState("");
  const [dispatch, setDispatch] = useState<DispatchFilter | "">("");
  // The Plan by hand prompt's delivery day, while it is open.
  const [handDate, setHandDate] = useState<string | null>(null);
  const [sel, setSel] = useState<Record<string, SelState>>({});
  const [manualSeq, setManualSeq] = useState(1);
  const [num26ft, setNum26ft] = useState(3);
  const [num53ft, setNum53ft] = useState(0);
  const [num3pl, setNum3pl] = useState(0);
  const departures = useTruckDepartures();
  // "optimize" = let the planner group/route (Build plan); "evaluate" = run
  // the dispatcher's own trip/truck assignment as-is (Evaluate my plan). Trip
  // / Truck pickers only show in evaluate mode so the default view doesn't
  // imply a fixed grouping the optimizer would ignore.
  const [mode, setMode] = useState<"optimize" | "evaluate">("optimize");
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [draftsAvailable, setDraftsAvailable] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteResult, setPasteResult] = useState<string | null>(null);
  const [pasteMissing, setPasteMissing] = useState<string[]>([]);
  // Cache of OrderSummary rows seen from the table load AND from paste-resolve,
  // so the "Selected" table view can show picked orders even when they aren't
  // in the current arrivals window (e.g. a pasted batch).
  const [summaries, setSummaries] = useState<Record<string, OrderSummary>>({});
  const [tableView, setTableView] = useState<"all" | "selected">("all");
  // Which cart cards are expanded to their full editor. DB/pasted orders start
  // collapsed (compact summary); manual orders start open since they need input.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const restored = useRef(false);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const loaded = await listOrders({
        days,
        search: search.trim() || undefined,
        region: region || undefined,
        dispatch: dispatch || undefined,
      });
      setOrders(loaded);
      setSummaries((prev) => {
        const next = { ...prev };
        for (const o of loaded) next[o.order_no] = o;
        return next;
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load only; re-filtering is an explicit "Refresh" action.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the saved drafts once. The backend 503s when persistence is off,
  // which flips the gate so the draft controls disable gracefully.
  useEffect(() => {
    void refreshDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the autosaved cart once, BEFORE the autosave effect can write —
  // the ref guard also survives React 18 StrictMode's double mount.
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const raw = localStorage.getItem(CART_AUTOSAVE_KEY);
      if (raw) applyCart(JSON.parse(raw));
    } catch {
      /* corrupt/disabled storage — start fresh */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave the cart on any composition change. Guarded so the empty initial
  // state can't clobber the saved blob before the restore effect runs.
  useEffect(() => {
    if (!restored.current) return;
    try {
      localStorage.setItem(CART_AUTOSAVE_KEY, JSON.stringify(serializeCart()));
    } catch {
      /* quota / disabled — best-effort */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, num26ft, num53ft, num3pl, departures.dep26, departures.dep53, departures.dep3pl, manualSeq]);

  function patch(id: string, p: Partial<SelState>) {
    setSel((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...p } } : prev));
  }

  async function addOrder(summary: OrderSummary, tripNo = 1, truckNo = 1) {
    const id = summary.order_no;
    setSel((prev) => ({
      ...prev,
      [id]: {
        manual: false,
        orderNo: summary.order_no,
        destZip: summary.dest_zip ?? "",
        customer: summary.customer_name,
        dimsSource: summary.dims_source,
        deadlineClock: "",
        arrivalClock: "",
        tripNo,
        truckNo,
        pallets: null,
        loadingPallets: true,
        edited: false,
      },
    }));
    try {
      const r = await getOrderPallets(summary.order_no);
      patch(id, { pallets: r.pallets.map(toCartPallet), loadingPallets: false });
    } catch {
      patch(id, { loadingPallets: false });
    }
  }

  function addManualOrder() {
    const id = `manual-${manualSeq}`;
    setManualSeq((n) => n + 1);
    setExpandedIds((prev) => new Set(prev).add(id)); // open for immediate entry
    setSel((prev) => ({
      ...prev,
      [id]: {
        manual: true,
        orderNo: "",
        destZip: "",
        customer: null,
        dimsSource: null,
        deadlineClock: "",
        arrivalClock: "",
        tripNo: 1,
        truckNo: 1,
        pallets: [STD_PALLET()],
        loadingPallets: false,
        edited: true,
      },
    }));
  }

  function removeOrder(id: string) {
    setSel((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExpandedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggle(summary: OrderSummary) {
    if (sel[summary.order_no]) removeOrder(summary.order_no);
    else void addOrder(summary);
  }

  function editPallet(id: string, i: number, field: keyof CartPallet, value: number) {
    setSel((prev) => {
      const s = prev[id];
      if (!s || !s.pallets) return prev;
      const pallets = s.pallets.map((p, idx) =>
        idx === i ? { ...p, [field]: value } : p,
      );
      return { ...prev, [id]: { ...s, pallets, edited: true } };
    });
  }

  function addPallet(id: string) {
    setSel((prev) => {
      const s = prev[id];
      if (!s) return prev;
      const last = s.pallets?.[s.pallets.length - 1];
      const next: CartPallet = last ? { ...last, source: "override" } : STD_PALLET();
      return { ...prev, [id]: { ...s, pallets: [...(s.pallets ?? []), next], edited: true } };
    });
  }

  function removePallet(id: string, i: number) {
    setSel((prev) => {
      const s = prev[id];
      if (!s || !s.pallets || s.pallets.length <= 1) return prev; // keep ≥1
      return {
        ...prev,
        [id]: { ...s, pallets: s.pallets.filter((_, idx) => idx !== i), edited: true },
      };
    });
  }

  const entries = Object.entries(sel);
  const dbSelectedCount = entries.filter(([, s]) => !s.manual).length;
  const totalPallets = entries.reduce((n, [, s]) => n + (s.pallets?.length ?? 0), 0);

  function buildPayload(): OrderSelectionPayload[] {
    return entries.map(([, s]) => {
      const o: OrderSelectionPayload = {
        order_no: s.orderNo.trim() || "MANUAL",
        // Trip numbers reset per truck in the UI, so qualify trip_id by truck to
        // keep it globally unique — the evaluator buckets trips by trip_id, and
        // truck_id groups a truck's trips. (Truck 1/Trip 1 vs Truck 2/Trip 1.)
        trip_id: `${s.truckNo}-${s.tripNo}`,
        truck_id: String(s.truckNo),
      };
      if (s.deadlineClock) o.deadline_clock = s.deadlineClock;
      if (s.arrivalClock) o.arrival_after_clock = s.arrivalClock;
      // The cart's ZIP is what gets routed, so send it for synced orders too —
      // the dispatcher may have corrected it after the address changed.
      if (s.destZip.trim()) o.dest_zip = s.destZip.trim();
      if ((s.manual || s.edited) && s.pallets) {
        o.pallets = s.pallets.map(palletToFt);
      }
      return o;
    });
  }

  const fleet = {
    num_26ft: num26ft,
    num_53ft: num53ft,
    departures: departures.list(num26ft, num53ft),
    num_3pl: num3pl,
    threepl_departures: departures.dep3pl.slice(0, num3pl),
  };

  // Block a doomed submit client-side so a backend 400 can't remount this view
  // and wipe the cart. With the time dropdown a malformed time is impossible;
  // the only field that can still go wrong is an order missing its ZIP (a
  // cleared synced ZIP would otherwise silently fall back to the old one).
  function validate(): string | null {
    for (const [, s] of entries) {
      if (!s.destZip.trim()) {
        return t("picker.validationZip", {
          name: s.orderNo.trim() || t("picker.unnamed"),
        });
      }
    }
    return null;
  }

  function build() {
    const err = validate();
    if (err) return setValidationError(err);
    setValidationError(null);
    onSubmit(buildPayload(), fleet);
  }
  function evaluate() {
    const err = validate();
    if (err) return setValidationError(err);
    setValidationError(null);
    onEvaluate(buildPayload(), fleet);
  }

  // The cart as a hand-built plan: Evaluate mode's trucks and trips become the
  // plan's (with no vehicles yet); otherwise every order starts unrouted.
  function handBuilt(planDate: string): HandBuiltPlan {
    const routeOf = (s: SelState) => `T${s.truckNo}-trip${s.tripNo - 1}`;
    const orders = entries.map(([, s]) => {
      const o: AddedOrderPayload = { order_no: s.orderNo.trim() || "MANUAL" };
      if (mode === "evaluate") o.route_id = routeOf(s);
      if (s.deadlineClock) o.deadline_clock = s.deadlineClock;
      if (s.arrivalClock) o.arrival_after_clock = s.arrivalClock;
      if (s.destZip.trim()) o.dest_zip = s.destZip.trim();
      if (s.manual) o.manual = true;
      if ((s.manual || s.edited) && s.pallets) o.pallets = s.pallets.map(palletToFt);
      return o;
    });
    if (mode !== "evaluate") return { orders, planDate };
    const trips = [...new Map(entries.map(([, s]) => [routeOf(s), s])).entries()].sort(
      ([, a], [, b]) => a.truckNo - b.truckNo || a.tripNo - b.tripNo,
    );
    const trucks = [...new Set(trips.map(([, s]) => s.truckNo))].map(
      (n): PlanTruck => ({
        truck_id: `T${n}`,
        carrier: "fleet",
        vehicle_id: null,
        driver_id: null,
        trailer_type: null,
        depart_min: null,
      }),
    );
    const routes = trips.map(
      ([routeId, s]): PlanRoute => ({
        route_id: routeId,
        truck_id: `T${s.truckNo}`,
        vehicle_id: null,
        driver_id: null,
        sequence_locked: false,
        carrier: "fleet",
        orders: [],
      }),
    );
    return { orders, planDate, document: { trucks, routes } };
  }

  function planByHand() {
    const err = validate();
    if (err) return setValidationError(err);
    setValidationError(null);
    // Today on this computer: the plan date offered by default.
    setHandDate(todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone));
  }

  async function onBulkAdd() {
    const parsed = parseOrderGroups(pasteText);
    if (parsed.length === 0) {
      setPasteResult(t("picker.pasteNothing"));
      return;
    }
    const nos = parsed.map((p) => p.no);
    const tripByNo = new Map(
      parsed.map((p) => [p.no.toUpperCase(), { tripNo: p.tripNo, truckNo: p.truckNo }]),
    );
    setPasteBusy(true);
    setPasteResult(null);
    setPasteMissing([]);
    try {
      const { found, missing } = await resolveOrders(nos);
      setSummaries((prev) => {
        const next = { ...prev };
        for (const o of found) next[o.order_no] = o;
        return next;
      });
      // Add in the PASTED order (not the resolve response's), so the cart —
      // and thus the evaluate delivery sequence within a trip — matches the
      // order the dispatcher listed them.
      const byNo = new Map(found.map((o) => [o.order_no.toUpperCase(), o]));
      let added = 0;
      let already = 0;
      for (const no of nos) {
        const o = byNo.get(no.toUpperCase());
        if (!o) continue; // unmatched — already surfaced in `missing`
        if (sel[o.order_no]) {
          already++;
          continue;
        }
        // Each blank-line block is a trip; trucks hold MAX_TRIPS_PER_TRUCK trips.
        const g = tripByNo.get(no.toUpperCase());
        void addOrder(o, g?.tripNo ?? 1, g?.truckNo ?? 1);
        added++;
      }
      setPasteMissing(missing);
      setPasteResult(
        t("picker.pasteResult", { added, already, missing: missing.length }),
      );
      if (added > 0) setPasteText("");
    } catch (e) {
      setPasteResult(e instanceof Error ? e.message : String(e));
    } finally {
      setPasteBusy(false);
    }
  }

  const tripCount = new Set(entries.map(([, s]) => s.tripNo)).size;
  // Cart orders already under way or on a plan: told before a plan is built.
  const onPlans = entries.filter(
    ([id, s]) => !s.manual && summaries[id]?.dispatch?.available_for_dispatch === false,
  ).length;

  // Table rows: all loaded arrivals, or just the picked orders (sourced from
  // the summary cache so a pasted batch shows even if it's outside the window).
  // Manual orders have no DB summary, so they live only in the cart on the right.
  const selectedRows = entries
    .map(([no]) => summaries[no])
    .filter((o): o is OrderSummary => !!o);
  const tableRows = tableView === "selected" ? selectedRows : orders;

  function clearAll() {
    if (entries.length > 0 && !window.confirm(t("picker.clearConfirm"))) {
      return;
    }
    setSel({});
    setExpandedIds(new Set());
    setValidationError(null);
  }

  function switchMode(next: "optimize" | "evaluate") {
    // Evaluating a plan means checking the client's own trip/truck sequence, so
    // don't carry the optimize selection into it — clear the builder on entering
    // evaluate so the dispatcher deliberately re-enters the orders to evaluate.
    if (next === "evaluate" && next !== mode) {
      setSel({});
      setExpandedIds(new Set());
      setValidationError(null);
      setPasteText("");
      setPasteResult(null);
      setPasteMissing([]);
    }
    setMode(next);
  }

  // ── Drafts: serialize the whole cart, persist it, and resume it ──────────

  function serializeCart(): Record<string, unknown> {
    const { dep26, dep53, dep3pl } = departures;
    return {
      version: 1,
      sel,
      fleet: { num26ft, num53ft, num3pl, dep26, dep53, dep3pl },
      manualSeq,
    };
  }

  function applyCart(payload: Record<string, unknown>) {
    const savedSel = (payload.sel as Record<string, SelState>) ?? {};
    // Trust the saved pallets (no re-fetch) but never resume mid-load.
    const clean: Record<string, SelState> = {};
    for (const [k, v] of Object.entries(savedSel)) {
      clean[k] = { ...v, loadingPallets: false };
    }
    const fleet =
      (payload.fleet as {
        num26ft?: number;
        num53ft?: number;
        num3pl?: number;
        dep26?: number[];
        dep53?: number[];
        dep3pl?: number[];
      }) ??
      {};
    setSel(clean);
    setNum26ft(fleet.num26ft ?? 3);
    setNum53ft(fleet.num53ft ?? 0);
    setNum3pl(fleet.num3pl ?? 0);
    departures.restore(fleet);
    setManualSeq((payload.manualSeq as number) ?? 1);
  }

  async function refreshDrafts() {
    try {
      setDrafts(await listDrafts());
      setDraftsAvailable(true);
      setDraftsError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("503")) setDraftsAvailable(false);
      else setDraftsError(msg);
    }
  }

  async function onSaveDraft() {
    const name = window.prompt(t("picker.draftNamePrompt"), new Date().toLocaleString());
    if (name == null) return; // cancelled
    const label = name.trim();
    if (!label) return;
    try {
      await saveDraft(label, serializeCart());
      await refreshDrafts();
      setDraftsOpen(true);
    } catch (e) {
      setDraftsError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onLoadDraft(id: string) {
    try {
      const d = await getDraft(id);
      applyCart(d.payload);
      setDraftsOpen(false);
    } catch (e) {
      setDraftsError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onDeleteDraft(id: string) {
    if (!window.confirm(t("picker.draftDeleteConfirm"))) return;
    try {
      await deleteDraft(id);
      await refreshDrafts();
    } catch (e) {
      setDraftsError(e instanceof Error ? e.message : String(e));
    }
  }

  // An order's status: quiet while available; else its plan's day and truck
  // (opening the plan there), or the day it was last attempted.
  function renderStatus(d: OrderDispatch) {
    if (d.status === "available") return null;
    const chip = (
      <span className={`plan-chip ${STATUS_TONE[d.status]}`}>{t(`status.${d.status}`)}</span>
    );
    const where =
      d.status_group === "pending" || d.status_group === "in_transit"
        ? [d.plan_date, d.truck_id].filter(Boolean).join(" · ")
        : d.plan_date;
    return d.plan_id && onOpenPlan ? (
      <button
        className="plan-issue-link"
        title={t("picker.openPlanAt")}
        onClick={() => onOpenPlan(d.plan_id!, d.truck_id ?? undefined)}
      >
        {chip} {where}
      </button>
    ) : (
      <span>
        {chip} {where}
      </span>
    );
  }

  return (
    <section className="starter" style={{ maxWidth: 1320 }}>
      <div className="starter-card" style={{ maxWidth: "none" }}>
        <h1>{t("picker.heading")}</h1>
        <p className="starter-desc">{t("picker.desc")}</p>

        <div style={layout}>
          {/* ── Left: filters + table ───────────────────────────────── */}
          <div style={{ flex: "3 1 680px", minWidth: 0 }}>
            <div className="config-row" style={{ alignItems: "flex-end" }}>
              <label className="control">
                <span className="control-label">{t("picker.arrivedWithin")}</span>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {[3, 7, 14, 30, 60, 0].map((d) => (
                    <option key={d} value={d}>
                      {d === 0 ? t("picker.allTime") : t("picker.daysN", { d })}
                    </option>
                  ))}
                </select>
              </label>
              <label className="control">
                <span className="control-label">{t("picker.regionLabel")}</span>
                <select value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="">{t("picker.regionAll")}</option>
                  {REGION_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`region.${r}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="control">
                <span className="control-label">{t("picker.statusFilter")}</span>
                <select
                  value={dispatch}
                  onChange={(e) => setDispatch(e.target.value as DispatchFilter | "")}
                >
                  <option value="">{t("picker.statusAll")}</option>
                  {DISPATCH_FILTERS.map((f) => (
                    <option key={f} value={f}>
                      {t(`picker.statusFilter.${f}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="control" style={{ flex: 1 }}>
                <span className="control-label">{t("picker.searchLabel")}</span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder={t("picker.searchPlaceholder")}
                />
              </label>
              <button className="btn" onClick={() => load()} disabled={loading}>
                {loading ? t("app.loading") : t("picker.search")}
              </button>
            </div>

            <div style={{ ...modeToggle, marginTop: 16 }}>
              <button
                style={modeBtn(tableView === "all")}
                onClick={() => setTableView("all")}
              >
                {t("picker.viewAll")}
              </button>
              <button
                style={modeBtn(tableView === "selected")}
                onClick={() => setTableView("selected")}
              >
                {t("picker.viewSelected", { n: dbSelectedCount })}
              </button>
            </div>

            {loadError && (
              <p className="upload-status error" style={{ marginTop: 8 }}>
                {loadError}
              </p>
            )}

            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}></th>
                    <th style={th}>Received</th>
                    <th style={th}>Order #</th>
                    <th style={th}>Customer</th>
                    <th style={th}>ZIP</th>
                    <th style={thNum}>Pallets</th>
                    <th style={thNum}>Cartons</th>
                    <th style={thNum}>Weight (kg)</th>
                    <th style={thNum}>CBM</th>
                    <th style={th}>Dims</th>
                    <th style={th}>{t("picker.statusCol")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && tableRows.length === 0 && (
                    <tr>
                      <td style={td} colSpan={11}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 10,
                            padding: "28px 0",
                          }}
                        >
                          <div className="spinner" />
                          <div style={{ fontWeight: 600 }}>
                            {t("picker.loadingOrders")}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.65 }}>
                            {t("picker.coldStartHint")}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {tableRows.length === 0 && !loading && (
                    <tr>
                      <td style={td} colSpan={11}>
                        {tableView === "selected"
                          ? t("picker.emptySelected")
                          : t("picker.emptyArrivals")}
                      </td>
                    </tr>
                  )}
                  {tableRows.map((o) => {
                    const picked = !!sel[o.order_no];
                    return (
                      <tr key={o.id} style={picked ? rowPicked : undefined}>
                        <td style={td}>
                          <input
                            type="checkbox"
                            checked={picked}
                            onChange={() => toggle(o)}
                          />
                        </td>
                        <td style={td}>{fmtDate(o.received_at)}</td>
                        <td style={tdMono}>{o.order_no}</td>
                        <td style={td}>{o.customer_name ?? "—"}</td>
                        <td style={tdMono}>{o.dest_zip ?? "—"}</td>
                        <td style={tdNum}>{o.received_pallets ?? "—"}</td>
                        <td style={tdNum}>{o.received_cartons ?? "—"}</td>
                        <td style={tdNum}>{fmtNum(o.weight_kg, 0)}</td>
                        <td style={tdNum}>{fmtNum(o.received_cbm, 2)}</td>
                        <td style={td}>
                          <span style={badge(o.dims_source)}>{o.dims_source}</span>
                        </td>
                        <td style={td}>{o.dispatch && renderStatus(o.dispatch)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p style={legend}>
              <span style={badge("measured")}>measured</span>{" "}
              {t("picker.legendMeasured")} ·{" "}
              <span style={badge("estimated")}>estimated</span>{" "}
              {t("picker.legendEstimated")}
            </p>
          </div>

          {/* ── Right: order builder ────────────────────────────────── */}
          <aside style={cartPanel}>
            <div style={cartHeader}>
              <strong>{t("picker.orderBuilder")}</strong>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ opacity: 0.7 }}>
                  {t("picker.cartOrders", { n: entries.length })} ·{" "}
                  {t("picker.cartPallets", { n: totalPallets })}
                </span>
                {entries.length > 0 && (
                  <button style={clearAllBtn} onClick={clearAll}>
                    {t("picker.clearAll")}
                  </button>
                )}
              </span>
            </div>

            <div style={modeToggle}>
              <button style={modeBtn(mode === "optimize")} onClick={() => switchMode("optimize")}>
                {t("picker.modeOptimize")}
              </button>
              <button style={modeBtn(mode === "evaluate")} onClick={() => switchMode("evaluate")}>
                {t("picker.modeEvaluate")}
              </button>
            </div>
            {mode === "evaluate" && (
              <p style={{ fontSize: 12, opacity: 0.7, margin: "0 0 8px" }}>
                {t("picker.evalInfo")}
              </p>
            )}

            <div style={draftBar}>
              <button
                style={{
                  ...draftSaveBtn,
                  ...(draftsAvailable ? {} : draftBtnDisabled),
                }}
                disabled={!draftsAvailable}
                title={
                  draftsAvailable
                    ? t("picker.saveDraftTip")
                    : t("picker.draftsUnavailableTip")
                }
                onClick={() => void onSaveDraft()}
              >
                {t("picker.saveDraft")}
              </button>
              <div style={{ position: "relative" }}>
                <button
                  style={{
                    ...draftBtn,
                    ...(draftsAvailable ? {} : draftBtnDisabled),
                  }}
                  disabled={!draftsAvailable}
                  onClick={() => setDraftsOpen((o) => !o)}
                >
                  {t("picker.draftsN", { n: drafts.length })}
                </button>
                {draftsOpen && (
                  <>
                    <div
                      style={draftBackdrop}
                      onClick={() => setDraftsOpen(false)}
                    />
                    <div style={draftPanel}>
                      {drafts.length === 0 && (
                        <div style={{ padding: 8, fontSize: 12, opacity: 0.6 }}>
                          {t("picker.noDrafts")}
                        </div>
                      )}
                      {drafts.map((d) => (
                        <div key={d.id} style={draftRow}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={draftName}>{d.label}</div>
                            <div style={{ fontSize: 11, opacity: 0.6 }}>
                              {fmtDraftTime(d.updated_at)}
                            </div>
                          </div>
                          <button
                            style={draftLoad}
                            onClick={() => void onLoadDraft(d.id)}
                          >
                            {t("picker.load")}
                          </button>
                          <button
                            style={draftDel}
                            title={t("picker.draftDeleteTip")}
                            onClick={() => void onDeleteDraft(d.id)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {draftsError && (
              <p
                className="upload-status error"
                style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}
              >
                {draftsError}
              </p>
            )}

            <button style={addOrderBtn} onClick={addManualOrder}>
              {t("picker.addManual")}
            </button>

            <button style={addOrderBtn} onClick={() => setPasteOpen((o) => !o)}>
              {pasteOpen ? t("picker.pasteOpen") : t("picker.pasteClosed")}
            </button>
            {pasteOpen && (
              <div style={{ marginBottom: 10 }}>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={t("picker.pastePlaceholder")}
                  rows={4}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                  }}
                />
                <button
                  className="btn"
                  disabled={pasteBusy}
                  style={{ marginTop: 6 }}
                  onClick={() => void onBulkAdd()}
                >
                  {pasteBusy ? t("picker.adding") : t("picker.addOrders")}
                </button>
                {pasteResult && (
                  <p className="upload-status" style={{ marginTop: 6, fontSize: 12 }}>
                    {pasteResult}
                  </p>
                )}
                {pasteMissing.length > 0 && (
                  <p
                    className="upload-status error"
                    style={{ marginTop: 4, fontSize: 12 }}
                  >
                    {t("picker.pasteNotFound", { list: pasteMissing.join(", ") })}
                  </p>
                )}
              </div>
            )}

            {entries.length === 0 && (
              <p style={{ opacity: 0.6, fontSize: 13, padding: "12px 2px" }}>
                {t("picker.emptyCart")}
              </p>
            )}

            <div style={cartList}>
              {entries.map(([id, s]) => {
                const open = expandedIds.has(id);
                const meta = [
                  s.manual ? s.destZip.trim() || "—" : s.customer ?? "—",
                  s.loadingPallets ? "…" : t("picker.cartPallets", { n: s.pallets?.length ?? 0 }),
                  ...(mode === "evaluate"
                    ? [t("picker.truckTripShort", { truck: s.truckNo, trip: s.tripNo })]
                    : []),
                ].join(" · ");
                return (
                  <div key={id} style={cartCard}>
                    <div style={cartCardHead}>
                      <button style={chevronBtn} aria-expanded={open} onClick={() => toggleExpanded(id)}>
                        {open ? "▾" : "▸"}
                      </button>
                      {s.manual && open ? (
                        <input
                          style={{ ...miniInput, ...orderNoText }}
                          placeholder={t("picker.manualPlaceholder")}
                          value={s.orderNo}
                          onChange={(e) => patch(id, { orderNo: e.target.value })}
                        />
                      ) : (
                        <span
                          style={{ ...orderNoText, cursor: "pointer" }}
                          onClick={() => toggleExpanded(id)}
                        >
                          {s.orderNo || t("picker.unnamed")}
                        </span>
                      )}
                      {!s.manual && summaries[id]?.dispatch?.available_for_dispatch === false && (
                        <span className={`plan-chip ${STATUS_TONE[summaries[id]!.dispatch!.status]}`}>
                          {t(`status.${summaries[id]!.dispatch!.status}`)}
                        </span>
                      )}
                      <button style={removeBtn} title={t("picker.removeOrderTip")} onClick={() => removeOrder(id)}>
                        ✕
                      </button>
                    </div>

                    {!open && (
                      <div style={cardMeta} onClick={() => toggleExpanded(id)}>
                        {meta}
                      </div>
                    )}

                    {open && (
                      <>
                        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            ZIP
                            <input
                              aria-label="ZIP"
                              style={{ ...miniInput, width: 90 }}
                              placeholder="91761"
                              value={s.destZip}
                              onChange={(e) => patch(id, { destZip: e.target.value })}
                            />
                            {s.manual ? (
                              <span style={badge("manual")}>{t("picker.manualBadge")}</span>
                            ) : (
                              <>
                                · {s.customer ?? "—"} ·{" "}
                                <span style={badge(s.dimsSource ?? "estimated")}>
                                  {s.dimsSource}
                                </span>
                              </>
                            )}
                          </span>
                        </div>

                        {mode === "evaluate" && (
                          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                            <label style={{ ...miniLabel, flex: 1 }}>
                              {t("picker.truck")}
                              <select
                                style={miniInput}
                                value={s.truckNo}
                                onChange={(e) => patch(id, { truckNo: Number(e.target.value) })}
                              >
                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                  <option key={n} value={n}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label style={{ ...miniLabel, flex: 1 }}>
                              {t("picker.trip")}
                              <select
                                style={miniInput}
                                value={s.tripNo}
                                onChange={(e) => patch(id, { tripNo: Number(e.target.value) })}
                              >
                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                  <option key={n} value={n}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                          <label style={miniLabel}>
                            {t("picker.notBefore")}
                            <TimeSelect
                              value={s.arrivalClock}
                              onChange={(v) => patch(id, { arrivalClock: v })}
                            />
                          </label>
                          <label style={miniLabel}>
                            {t("picker.deliverBy")}
                            <TimeSelect
                              value={s.deadlineClock}
                              onChange={(v) => patch(id, { deadlineClock: v })}
                            />
                          </label>
                        </div>

                        {s.loadingPallets && <span style={{ fontSize: 12 }}>{t("picker.loadingPallets")}</span>}
                        {s.pallets && (
                          <div>
                            {s.dimsSource === "estimated" && !s.edited && (
                              <div style={estimatedWarning}>
                                ⚠ {t("picker.estimatedDimsWarning")}
                              </div>
                            )}
                            <div style={palletHeader}>
                              <span style={{ flex: "0 0 22px" }}></span>
                              <span style={dimHead}>L</span>
                              <span style={dimHead}>W</span>
                              <span style={dimHead}>H</span>
                              <span style={dimHead}>kg</span>
                              <span style={{ flex: "0 0 20px" }}></span>
                            </div>
                            {s.pallets.map((p, i) => (
                              <div key={i} style={palletRow}>
                                <span style={{ flex: "0 0 22px", fontSize: 12 }}>P{i + 1}</span>
                                <DimInput value={p.length_cm} onChange={(v) => editPallet(id, i, "length_cm", v)} />
                                <DimInput value={p.width_cm} onChange={(v) => editPallet(id, i, "width_cm", v)} />
                                <DimInput value={p.height_cm} onChange={(v) => editPallet(id, i, "height_cm", v)} />
                                <DimInput value={p.weight_kg} onChange={(v) => editPallet(id, i, "weight_kg", v)} />
                                <button
                                  style={palletRemove}
                                  title={t("picker.removePalletTip")}
                                  disabled={s.pallets!.length <= 1}
                                  onClick={() => removePallet(id, i)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            <button style={addPalletBtn} onClick={() => addPallet(id)}>
                              {t("picker.addPallet")}
                            </button>
                            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                              {t("picker.palletUnits")}
                              {s.edited ? ` · ${t("picker.edited")}` : ""}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ borderTop: "1px solid rgba(0,0,0,0.1)", paddingTop: 12 }}>
              <div style={fleetRow}>
                <Select label={t("fleet.available26")} value={num26ft} set={setNum26ft} max={10} />
                <Select label={t("fleet.available53")} value={num53ft} set={setNum53ft} max={3} />
                <Select label={t("fleet.available3pl")} value={num3pl} set={setNum3pl} max={4} />
              </div>
              <TruckDepartures
                num26ft={num26ft}
                num53ft={num53ft}
                num3pl={num3pl}
                departures={departures}
              />
              {onPlans > 0 && (
                <p className="plan-muted" style={{ marginTop: 12 }}>
                  {t("picker.onPlansNote", { n: onPlans })}
                </p>
              )}
              {mode === "optimize" ? (
                <button
                  className="btn btn-primary btn-lg"
                  style={{ width: "100%", marginTop: 12 }}
                  disabled={
                    entries.length === 0 ||
                    num26ft + num53ft < 1 ||
                    num26ft + num53ft > 10
                  }
                  onClick={build}
                >
                  {t("picker.buildPlan", { n: entries.length })}
                </button>
              ) : (
                <button
                  className="btn btn-primary btn-lg"
                  style={{ width: "100%", marginTop: 12 }}
                  disabled={entries.length === 0}
                  title={t("picker.evaluateTip")}
                  onClick={evaluate}
                >
                  {t("picker.evaluatePlan", { n: tripCount })}
                </button>
              )}
              {onPlanByHand && (
                <button
                  className="btn btn-secondary"
                  style={{ width: "100%", marginTop: 8 }}
                  disabled={entries.length === 0}
                  title={t("picker.planByHandTip")}
                  onClick={planByHand}
                >
                  {t("picker.planByHand", { n: entries.length })}
                </button>
              )}
              {validationError && (
                <p className="upload-status error" style={{ marginTop: 8 }}>
                  {validationError}
                </p>
              )}
              {errorMessage && (
                <p className="upload-status error" style={{ marginTop: 8 }}>
                  {errorMessage}
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
      {handDate !== null && onPlanByHand && (
        <Dialog title={t("picker.planByHandTitle")} busy={false} onCancel={() => setHandDate(null)}>
          <p id="plan-dialog-desc">
            {mode === "evaluate" ? t("picker.planByHandTrips") : t("picker.planByHandPool")}
          </p>
          {onPlans > 0 && <p className="plan-muted">{t("picker.planByHandOnPlans", { n: onPlans })}</p>}
          <label className="control">
            <span className="control-label">{t("picker.planDate")}</span>
            <input
              type="date"
              value={handDate}
              onChange={(e) => setHandDate(e.target.value)}
            />
          </label>
          <div className="plan-dialog-actions">
            <button className="btn btn-ghost" onClick={() => setHandDate(null)}>
              {t("picker.cancel")}
            </button>
            <button
              className="btn btn-primary"
              disabled={!handDate}
              onClick={() => {
                const plan = handBuilt(handDate);
                setHandDate(null);
                onPlanByHand(plan);
              }}
            >
              {t("picker.createPlan")}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

function TimeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const t = useT();
  return (
    <select style={miniInput} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t("picker.timeAny")}</option>
      {TIME_SLOTS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

function DimInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      min="0"
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={dimInput}
    />
  );
}

// Compact fleet picker for the builder footer — label inline beside a narrow
// select so both (26 ft / 53 ft) sit on one row instead of wrapping.
function Select({
  label,
  value,
  set,
  max,
}: {
  label: string;
  value: number;
  set: (n: number) => void;
  max: number;
}) {
  return (
    <label style={fleetControl}>
      <span style={fleetLabel}>{label}</span>
      <select style={fleetSelect} value={value} onChange={(e) => set(Number(e.target.value))}>
        {Array.from({ length: max + 1 }, (_, i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
    </label>
  );
}

const fleetRow: CSSProperties = { display: "flex", gap: 10, alignItems: "center" };
const fleetControl: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  flex: 1,
  minWidth: 0,
};
const fleetLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--muted)",
  whiteSpace: "nowrap",
};
const fleetSelect: CSSProperties = {
  flex: 1,
  minWidth: 40,
  padding: "5px 6px",
  border: "1px solid var(--border-strong)",
  borderRadius: 6,
  background: "var(--surface)",
  fontSize: 13,
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 16);
}

function fmtNum(n: number | null, decimals: number): string {
  if (n == null) return "—";
  return n.toFixed(decimals);
}

// ── inline styles ─────────────────────────────────────────────────────────
const layout: CSSProperties = {
  display: "flex",
  gap: 24,
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const tableWrap: CSSProperties = {
  marginTop: 12,
  maxHeight: "calc(100vh - 230px)",
  overflow: "auto",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 8,
};
const table: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  position: "sticky",
  top: 0,
  background: "var(--surface, #fff)",
  borderBottom: "1px solid rgba(0,0,0,0.12)",
  whiteSpace: "nowrap",
};
const thNum: CSSProperties = { ...th, textAlign: "right" };
const td: CSSProperties = {
  padding: "6px 10px",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
};
const tdNum: CSSProperties = { ...td, textAlign: "right" };
const tdMono: CSSProperties = { ...td, fontFamily: "ui-monospace, monospace" };
const rowPicked: CSSProperties = { background: "rgba(56,132,255,0.10)" };
const legend: CSSProperties = { fontSize: 12, opacity: 0.75, marginTop: 8 };

const cartPanel: CSSProperties = {
  flex: "1 1 310px",
  position: "sticky",
  top: 12,
  // Cap to the viewport so the builder never runs off the page; the order list
  // (cartList) is the only part that scrolls, keeping the header controls and the
  // Build/Evaluate footer pinned and always visible no matter how many orders.
  // No overflow:hidden here — it would clip the absolutely-positioned drafts
  // dropdown; cartList owns the scroll instead.
  maxHeight: "calc(100vh - 24px)",
  display: "flex",
  flexDirection: "column",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 10,
  padding: 14,
  background: "rgba(0,0,0,0.015)",
};
const cartHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: 14,
  marginBottom: 8,
};
const modeToggle: CSSProperties = {
  display: "flex",
  gap: 4,
  marginBottom: 10,
  background: "rgba(0,0,0,0.05)",
  padding: 3,
  borderRadius: 8,
};
const clearAllBtn: CSSProperties = {
  border: "1px solid rgba(0,0,0,0.18)",
  background: "transparent",
  borderRadius: 6,
  fontSize: 12,
  padding: "3px 9px",
  cursor: "pointer",
  color: "var(--warn, #ef4444)",
  fontWeight: 600,
};

function modeBtn(active: boolean): CSSProperties {
  return {
    flex: 1,
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    background: active ? "var(--surface, #fff)" : "transparent",
    color: active ? "var(--primary, #1F3864)" : "inherit",
    boxShadow: active ? "var(--shadow, 0 1px 3px rgba(0,0,0,0.12))" : "none",
  };
}
const addOrderBtn: CSSProperties = {
  border: "1px solid var(--accent)",
  background: "var(--accent-soft)",
  color: "var(--accent)",
  fontWeight: 600,
  borderRadius: 8,
  fontSize: 13,
  padding: "6px 10px",
  cursor: "pointer",
  marginBottom: 10,
};
const cartList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginBottom: 12,
  // The sole scroll region inside the bounded panel. minHeight:0 lets a flex
  // child shrink below its content so it actually scrolls; paddingRight keeps the
  // scrollbar off the cards.
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: 4,
};
const cartCard: CSSProperties = {
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 8,
  padding: "7px 10px",
  background: "var(--surface, #fff)",
};
const cartCardHead: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  marginBottom: 2,
};
const chevronBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 11,
  lineHeight: 1,
  padding: 0,
  width: 14,
  flex: "0 0 auto",
  color: "var(--primary, #1F3864)",
};
const cardMeta: CSSProperties = {
  fontSize: 12,
  opacity: 0.7,
  cursor: "pointer",
  paddingLeft: 22,
};
// Order number — the card's anchor; bolded and in the brand color so it reads
// at a glance in a long builder list.
const orderNoText: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontFamily: "ui-monospace, monospace",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: "0.02em",
  color: "var(--primary, #1F3864)",
};
const removeBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 14,
  opacity: 0.5,
  lineHeight: 1,
  flex: "0 0 auto",
};
const miniLabel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: 11,
  gap: 2,
  flex: 1,
};
const miniInput: CSSProperties = { width: "100%", boxSizing: "border-box" };
const palletHeader: CSSProperties = { display: "flex", gap: 6, marginBottom: 2 };
const estimatedWarning: CSSProperties = {
  fontSize: 12,
  padding: "6px 10px",
  borderRadius: 6,
  background: "rgba(210,150,20,0.14)",
  color: "#8a6300",
  border: "1px solid rgba(210,150,20,0.45)",
  marginBottom: 6,
};
const dimHead: CSSProperties = {
  flex: 1,
  fontSize: 11,
  opacity: 0.6,
  textAlign: "center",
};
const palletRow: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  marginBottom: 4,
};
const dimInput: CSSProperties = {
  flex: 1,
  width: "100%",
  boxSizing: "border-box",
  textAlign: "right",
  fontSize: 12,
  padding: "2px 4px",
};
const palletRemove: CSSProperties = {
  flex: "0 0 20px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 12,
  opacity: 0.45,
  lineHeight: 1,
  padding: 0,
};
const addPalletBtn: CSSProperties = {
  marginTop: 4,
  border: "1px dashed rgba(0,0,0,0.25)",
  background: "transparent",
  borderRadius: 6,
  fontSize: 12,
  padding: "3px 8px",
  cursor: "pointer",
};

function badge(source: string): CSSProperties {
  const palette: Record<string, [string, string]> = {
    measured: ["rgba(34,160,90,0.15)", "#1c7a44"],
    estimated: ["rgba(210,150,20,0.18)", "#8a6300"],
    manual: ["rgba(56,132,255,0.15)", "#1f5fbf"],
  };
  const [bg, fg] = palette[source] ?? palette.estimated;
  return {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 999,
    background: bg,
    color: fg,
    whiteSpace: "nowrap",
  };
}

function fmtDraftTime(iso: string): string {
  return iso ? new Date(iso).toLocaleString() : "";
}

// ── draft toolbar styles ────────────────────────────────────────────────────
const draftBar: CSSProperties = { display: "flex", gap: 8, marginBottom: 10 };
const draftBtn: CSSProperties = {
  fontSize: 13,
  padding: "6px 10px",
  border: "1px solid rgba(0,0,0,0.18)",
  borderRadius: 6,
  background: "var(--surface, #fff)",
  cursor: "pointer",
};
const draftSaveBtn: CSSProperties = {
  ...draftBtn,
  background: "var(--accent, #3b82f6)",
  border: "1px solid var(--accent, #3b82f6)",
  color: "#fff",
  fontWeight: 600,
};
const draftBtnDisabled: CSSProperties = { opacity: 0.45, cursor: "not-allowed" };
const draftBackdrop: CSSProperties = { position: "fixed", inset: 0, zIndex: 15 };
const draftPanel: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  width: 280,
  maxHeight: 260,
  overflowY: "auto",
  background: "var(--surface, #fff)",
  border: "1px solid rgba(0,0,0,0.18)",
  borderRadius: 8,
  boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
  zIndex: 20,
  padding: 4,
};
const draftRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
};
const draftName: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const draftLoad: CSSProperties = {
  fontSize: 12,
  padding: "3px 8px",
  border: "1px solid rgba(0,0,0,0.18)",
  borderRadius: 5,
  background: "var(--surface, #fff)",
  cursor: "pointer",
  flex: "0 0 auto",
};
const draftDel: CSSProperties = {
  fontSize: 12,
  width: 24,
  height: 24,
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 5,
  background: "var(--surface, #fff)",
  cursor: "pointer",
  flex: "0 0 auto",
};
